import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
    Alert,
    Anchor,
    Badge,
    Box,
    Button,
    Card,
    Center,
    Checkbox,
    Divider,
    Group,
    List,
    Loader,
    Modal,
    Pagination,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconPencil,
    IconPlus,
    IconSearch,
    IconTrash,
} from '@tabler/icons-react';
import type { CatalogEntry, SortOrder, VendorSortField } from '../api/types';
import {
    bulkDeleteCompanies,
    createVendor,
    listVendorProviders,
    listVendors,
    updateVendor,
    type BulkDeleteResult,
} from '../api/vendors';
import { SortableTh } from '../components/SortableTh';
import { NoTenantsFilter } from '../components/NoTenantsFilter';
import { OsintReportFilter } from '../components/OsintReportFilter';
import { ProviderFilter } from '../components/ProviderFilter';
import { VendorFormModal, type VendorFormValues } from '../components/VendorFormModal';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { safeHttpUrl } from '../utils/url';

/** Trim an optional text field; empty → undefined so we omit it from the payload. */
const optional = (value: string): string | undefined => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
};

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = ['25', '50', '100', '250'];

/**
 * Vendor list dashboard view. Searchable, paginated table of catalog
 * entries with CPE-mapping and CVE-match counts. Rendered at `/catalog`; a row
 * click navigates to `/catalog/$candidateId` (detail is keyed on the numeric
 * candidate id — the canonical id for the detail fetch and sibling cards,
 * VI-472/VI-473).
 *
 * Reviewers can multi-select rows and bulk-delete them: each selected entry
 * removes the WHOLE owning company — its `catalog_company` row, every
 * `catalog_product` under it, and every `catalog_product_candidate` tied to it
 * (soft-deleted). Selecting two rows of the same company is safe: the second
 * cleanly no-ops (reported as "already removed") since the first already took
 * the whole company.
 */
export const VendorListView = (): React.JSX.Element => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchInput, 300);
    const [page, setPage] = useState(1); // 1-indexed for Mantine
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortField, setSortField] = useState<VendorSortField | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
    const [noTenantsOnly, setNoTenantsOnly] = useState(false);
    const [osintReportOnly, setOsintReportOnly] = useState(false);

    // Ids (catalog_product_candidate_id) checked on the CURRENT page. Selection
    // is cleared whenever the page/search/sort changes, so this set only holds
    // ids visible right now.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [bulkResult, setBulkResult] = useState<BulkDeleteResult | null>(null);
    // Snapshot of candidate id → display name captured at delete time, so the
    // result summary can label failed rows even after the list refetches.
    const [deleteNames, setDeleteNames] = useState<Map<number, string>>(() => new Map());

    const offset = (page - 1) * pageSize;

    const { data, isLoading, error } = useQuery({
        queryKey: [
            'vendors',
            debouncedSearch,
            page,
            pageSize,
            sortField,
            sortOrder,
            selectedProviders,
            noTenantsOnly,
            osintReportOnly,
        ],
        queryFn: () =>
            listVendors({
                q: debouncedSearch || undefined,
                include_stats: true,
                // Company Catalog lists curated/promoted vendors only (VI-510);
                // /api/catalog stays unfiltered by default for other consumers.
                curated: true,
                providers: selectedProviders.length > 0 ? selectedProviders : undefined,
                noTenants: noTenantsOnly || undefined,
                hasOsintReport: osintReportOnly || undefined,
                limit: pageSize,
                offset,
                sort: sortField ?? undefined,
                order: sortField ? sortOrder : undefined,
            }),
    });

    // Distinct providers present in the curated catalog — drives the filter's
    // checkbox options (only what's in the DB) + the "No provider" option.
    const { data: providerOptions } = useQuery({
        queryKey: ['vendor-providers'],
        queryFn: listVendorProviders,
    });

    const vendors = useMemo(() => data?.data ?? [], [data]);
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    // Reset selection + any prior summary when the visible page changes, so a
    // stale selection never leaks across pages, searches, or sorts.
    const resetOnPageChange = useCallback(() => {
        clearSelection();
        setBulkResult(null);
    }, [clearSelection]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // Changing the provider filter narrows the whole result set, so reset to
    // page 1 and clear the page-scoped selection (same as search/sort).
    const handleProvidersChange = useCallback(
        (providers: string[]) => {
            setSelectedProviders(providers);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // "No tenants" narrows the whole result set too → same reset as the provider filter.
    const handleNoTenantsChange = useCallback(
        (value: boolean) => {
            setNoTenantsOnly(value);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // "Has OSINT report" narrows the whole result set too → same reset.
    const handleOsintReportChange = useCallback(
        (value: boolean) => {
            setOsintReportOnly(value);
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    const handleSelect = useCallback(
        (candidateId: number) => {
            void navigate({ to: '/catalog/$candidateId', params: { candidateId } });
        },
        [navigate],
    );

    // Toggle direction when re-clicking the active column; otherwise switch
    // columns and start ascending. Either way reset to page 1 (new top of the
    // ordering) and clear the page-scoped selection.
    const handleSort = useCallback(
        (field: VendorSortField): void => {
            if (sortField === field) {
                setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortField(field);
                setSortOrder('asc');
            }
            setPage(1);
            resetOnPageChange();
        },
        [sortField, resetOnPageChange],
    );

    const handlePageChange = useCallback(
        (next: number) => {
            setPage(next);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    // Changing the page size resets to page 1 — the old offset is meaningless
    // under a different size — and clears the page-scoped selection.
    const handlePageSizeChange = useCallback(
        (value: string | null): void => {
            if (!value) return;
            setPageSize(Number(value));
            setPage(1);
            resetOnPageChange();
        },
        [resetOnPageChange],
    );

    const toggleRow = useCallback((id: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    const pageIds = useMemo(() => vendors.map((v) => v.catalog_product_candidate_id), [vendors]);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    const somePageSelected = pageIds.some((id) => selectedIds.has(id));

    const toggleAllOnPage = useCallback(
        (checked: boolean) => {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                for (const id of pageIds) {
                    if (checked) {
                        next.add(id);
                    } else {
                        next.delete(id);
                    }
                }
                return next;
            });
        },
        [pageIds],
    );

    const bulkMutation = useMutation({
        mutationFn: (ids: number[]) => bulkDeleteCompanies(ids),
        onSuccess: (result) => {
            setBulkResult(result);
            clearSelection();
            // Refresh the Company Catalog (rows drop) AND the Candidate Catalog,
            // whose underlying candidate rows are soft-deleted by the same op.
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            notifications.show({
                color: result.failed.length > 0 ? 'red' : 'green',
                title: 'Bulk delete complete',
                message: summarizeResult(result),
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Bulk delete failed',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const { isPending } = bulkMutation;

    const handleConfirm = () => {
        setConfirmOpen(false);
        setBulkResult(null);
        const selected = vendors.filter((v) => selectedIds.has(v.catalog_product_candidate_id));
        setDeleteNames(new Map(selected.map((v) => [v.catalog_product_candidate_id, v.name])));
        bulkMutation.mutate(selected.map((v) => v.catalog_product_candidate_id));
    };

    const selectedCount = selectedIds.size;

    // Admin-only add/edit (VI-783). Enforcement is server-side + prod-only; this
    // just hides the affordances for non-admins. The Edit action operates on the
    // single selected row.
    const { data: adminStatus } = useIsAdmin();
    const isAdmin = adminStatus?.is_admin ?? false;
    const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
    const [editingVendor, setEditingVendor] = useState<CatalogEntry | null>(null);

    const createMutation = useMutation({
        mutationFn: (values: VendorFormValues) =>
            createVendor({
                name: values.name.trim(),
                // Identity tuple is optional on create — omit blanks so the server
                // mints a MANUAL provider / uuid org / `default` slug for them.
                provider_abbreviation: optional(values.provider_abbreviation),
                organization_id: optional(values.organization_id),
                slug: optional(values.slug),
                // is_default is a real boolean; the rest are omitted when blank.
                is_default: values.is_default,
                domain: optional(values.domain),
                website_url: optional(values.website_url),
                open_trust_url: optional(values.open_trust_url),
                trust_center_url: optional(values.trust_center_url),
                logo: optional(values.logo),
                favicon: optional(values.favicon),
                product_name: optional(values.product_name),
                metadata: optional(values.metadata),
            }),
        onSuccess: (created) => {
            setFormMode(null);
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            notifications.show({
                color: 'green',
                title: 'Vendor added',
                message: `${created.name} was added to the catalog.`,
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not add vendor',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ candidateId, values }: { candidateId: number; values: VendorFormValues }) =>
            updateVendor(candidateId, {
                // Required fields — always present (validated non-empty in the form).
                name: values.name.trim(),
                provider_abbreviation: values.provider_abbreviation.trim(),
                organization_id: values.organization_id.trim(),
                slug: values.slug.trim(),
                is_default: values.is_default,
                // Clearable fields — send trimmed value INCLUDING '' so the server clears (→ null).
                domain: values.domain.trim(),
                website_url: values.website_url.trim(),
                open_trust_url: values.open_trust_url.trim(),
                trust_center_url: values.trust_center_url.trim(),
                logo: values.logo.trim(),
                favicon: values.favicon.trim(),
                metadata: values.metadata.trim(),
                // Product name can't be blank server-side (min 1); empty leaves it unchanged.
                product_name: optional(values.product_name),
            }),
        onSuccess: (updated) => {
            setFormMode(null);
            setEditingVendor(null);
            clearSelection();
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            void queryClient.invalidateQueries({
                queryKey: ['vendor', updated.catalog_product_candidate_id],
            });
            notifications.show({
                color: 'green',
                title: 'Vendor updated',
                message: `${updated.name} was updated.`,
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not update vendor',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const selectedVendor =
        selectedCount === 1
            ? (vendors.find((v) => selectedIds.has(v.catalog_product_candidate_id)) ?? null)
            : null;

    const handleFormSubmit = (values: VendorFormValues): void => {
        if (formMode === 'create') {
            createMutation.mutate(values);
        } else if (editingVendor) {
            updateMutation.mutate({
                candidateId: editingVendor.catalog_product_candidate_id,
                values,
            });
        }
    };

    return (
        <Stack gap="md" flex={1} mih={0} data-testid="VendorListView">
            <Title order={2}>Company Catalog</Title>
            <Group gap="sm" align="flex-start" wrap="nowrap">
                <TextInput
                    flex={1}
                    placeholder="Search vendors by name or domain…"
                    leftSection={<IconSearch size={16} />}
                    value={searchInput}
                    onChange={(e) => {
                        handleSearchChange(e.currentTarget.value);
                    }}
                    data-testid="VendorSearchInput"
                />
                <ProviderFilter
                    value={selectedProviders}
                    onChange={handleProvidersChange}
                    options={providerOptions?.providers ?? []}
                    hasNoProvider={providerOptions?.hasNoProvider ?? false}
                />
                <NoTenantsFilter value={noTenantsOnly} onChange={handleNoTenantsChange} />
                <OsintReportFilter value={osintReportOnly} onChange={handleOsintReportChange} />
                {isAdmin ? (
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={() => {
                            setEditingVendor(null);
                            setFormMode('create');
                        }}
                        data-testid="VendorAddButton"
                    >
                        Add vendor
                    </Button>
                ) : null}
            </Group>

            {bulkResult && !isPending ? (
                <BulkDeleteSummary
                    result={bulkResult}
                    names={deleteNames}
                    onDismiss={() => {
                        setBulkResult(null);
                    }}
                />
            ) : null}

            {error ? (
                <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="red"
                    title="Failed to load vendors"
                    data-testid="VendorListError"
                >
                    {error instanceof Error ? error.message : 'Unknown error'}
                </Alert>
            ) : isLoading ? (
                <Center flex={1}>
                    <Loader />
                </Center>
            ) : vendors.length === 0 ? (
                <Center flex={1}>
                    <Text c="dimmed" data-testid="VendorListEmpty">
                        No vendors found.
                    </Text>
                </Center>
            ) : (
                <>
                    {selectedCount > 0 ? (
                        <Card withBorder radius="md" padding="sm" data-testid="VendorBulkActionBar">
                            <Group justify="space-between" align="center">
                                <Text size="sm" fw={500}>
                                    {selectedCount} selected on this page
                                </Text>
                                <Group gap="sm">
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        onClick={clearSelection}
                                        disabled={isPending}
                                    >
                                        Clear
                                    </Button>
                                    {isAdmin && selectedCount === 1 && selectedVendor ? (
                                        <Button
                                            variant="light"
                                            leftSection={<IconPencil size={16} />}
                                            size="sm"
                                            onClick={() => {
                                                setEditingVendor(selectedVendor);
                                                setFormMode('edit');
                                            }}
                                            data-testid="VendorEditButton"
                                        >
                                            Edit
                                        </Button>
                                    ) : null}
                                    <Button
                                        color="red"
                                        leftSection={<IconTrash size={16} />}
                                        size="sm"
                                        onClick={() => {
                                            setConfirmOpen(true);
                                        }}
                                        loading={isPending}
                                        data-testid="VendorBulkDeleteButton"
                                    >
                                        Delete selected ({selectedCount})
                                    </Button>
                                </Group>
                            </Group>
                        </Card>
                    ) : null}

                    <Table.ScrollContainer minWidth={640} maxHeight="100%" flex={1} mih={0}>
                        <Table stickyHeader highlightOnHover striped data-testid="VendorListTable">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={44}>
                                        <Checkbox
                                            aria-label="Select all vendors on this page"
                                            checked={allPageSelected}
                                            indeterminate={somePageSelected && !allPageSelected}
                                            onChange={(e) => {
                                                toggleAllOnPage(e.currentTarget.checked);
                                            }}
                                            disabled={isPending}
                                            data-testid="VendorSelectAll"
                                        />
                                    </Table.Th>
                                    {/* Numeric catalog_product_candidate_id — the shared
                                        join key, shown first for parity with the Candidate
                                        Catalog. Not sortable: CatalogSortField.ID sorts the
                                        compound provider:org:slug string, not this id. */}
                                    <Table.Th w={70}>ID</Table.Th>
                                    <SortableTh<VendorSortField>
                                        label="Name"
                                        field="name"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<VendorSortField>
                                        label="Provider"
                                        field="provider_abbreviation"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<VendorSortField>
                                        label="Product"
                                        field="product_name"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<VendorSortField>
                                        label="Domain"
                                        field="domain"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <Table.Th w={90}>Is Default</Table.Th>
                                    <SortableTh<VendorSortField>
                                        label="Trust Center URL"
                                        field="trust_center_url"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableTh<VendorSortField>
                                        label="Tenants"
                                        field="tenant_count"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                        width={90}
                                    />
                                    <SortableTh<VendorSortField>
                                        label="CPEs"
                                        field="cpe_mapping_count"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                        width={90}
                                    />
                                    <SortableTh<VendorSortField>
                                        label="CVEs"
                                        field="cve_match_count"
                                        activeField={sortField}
                                        order={sortOrder}
                                        onSort={handleSort}
                                        width={90}
                                    />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {vendors.map((v) => (
                                    <VendorRow
                                        key={v.catalog_product_candidate_id}
                                        vendor={v}
                                        selected={selectedIds.has(v.catalog_product_candidate_id)}
                                        onToggle={toggleRow}
                                        onSelect={handleSelect}
                                        disabled={isPending}
                                    />
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    <Group justify="space-between" align="center">
                        <Group gap="sm" align="center">
                            <Text size="sm" c="dimmed">
                                Showing {offset + 1}–{Math.min(offset + vendors.length, total)} of{' '}
                                {total}
                            </Text>
                            <Select
                                aria-label="Rows per page"
                                data={PAGE_SIZE_OPTIONS}
                                value={String(pageSize)}
                                onChange={handlePageSizeChange}
                                allowDeselect={false}
                                w={90}
                                size="xs"
                                data-testid="VendorPageSize"
                            />
                        </Group>
                        <Pagination value={page} onChange={handlePageChange} total={pageCount} />
                    </Group>
                </>
            )}

            <Modal
                opened={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                }}
                title="Delete selected companies"
                centered
                data-testid="VendorBulkDeleteConfirmModal"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Delete {selectedCount} selected{' '}
                        {selectedCount === 1 ? 'company' : 'companies'}? This removes each company
                        along with all of its products and candidate records.
                    </Text>
                    <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
                        This can’t be undone from here. Rows belonging to the same company are
                        removed together.
                    </Alert>
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={() => {
                                setConfirmOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            leftSection={<IconTrash size={16} />}
                            onClick={handleConfirm}
                            data-testid="VendorBulkDeleteConfirmButton"
                        >
                            Delete {selectedCount}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <VendorFormModal
                opened={formMode !== null}
                mode={formMode ?? 'create'}
                initial={
                    formMode === 'edit' && editingVendor
                        ? {
                              name: editingVendor.name,
                              provider_abbreviation: editingVendor.provider_abbreviation ?? '',
                              organization_id: editingVendor.organization_id ?? '',
                              domain: editingVendor.domain ?? '',
                              website_url: editingVendor.website_url ?? '',
                              open_trust_url: editingVendor.open_trust_url ?? '',
                              trust_center_url: editingVendor.trust_center_url ?? '',
                              logo: editingVendor.logo ?? '',
                              favicon: editingVendor.favicon ?? '',
                              product_name: editingVendor.product_name ?? '',
                              slug: editingVendor.slug ?? '',
                              is_default: editingVendor.is_default,
                              metadata: editingVendor.metadata ?? '',
                          }
                        : undefined
                }
                submitting={createMutation.isPending || updateMutation.isPending}
                onClose={() => {
                    setFormMode(null);
                    setEditingVendor(null);
                }}
                onSubmit={handleFormSubmit}
            />
        </Stack>
    );
};

/**
 * Human-readable one-liner for the completion toast.
 */
function summarizeResult(result: BulkDeleteResult): string {
    const parts = [`Deleted ${result.deleted.length}`, `${result.skipped.length} already removed`];
    if (result.failed.length > 0) {
        parts.push(`${result.failed.length} failed`);
    }
    return `${parts.join(', ')}.`;
}

interface BulkDeleteSummaryProps {
    result: BulkDeleteResult;
    /** candidate id → display name captured at delete time (the list may have refetched). */
    names: Map<number, string>;
    onDismiss: () => void;
}

/**
 * Inline panel shown after a bulk run: the deleted/skipped/failed tallies plus
 * per-id detail for anything that didn't delete cleanly.
 */
const BulkDeleteSummary = ({
    result,
    names,
    onDismiss,
}: BulkDeleteSummaryProps): React.JSX.Element => {
    const { deleted, skipped, failed } = result;
    const label = (id: number): string => names.get(id) ?? `#${String(id)}`;

    return (
        <Card withBorder radius="md" padding="lg" data-testid="VendorBulkDeleteSummary">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <Box>
                        <Title order={4}>Bulk delete complete</Title>
                        <Text size="sm" c="dimmed" mt={4}>
                            Each deleted company took its products and candidate records with it.
                        </Text>
                    </Box>
                    <Button
                        variant="subtle"
                        size="xs"
                        onClick={onDismiss}
                        data-testid="VendorBulkDeleteSummaryDismiss"
                    >
                        Dismiss
                    </Button>
                </Group>

                <Group gap="sm">
                    <Badge color="green" variant="light" data-testid="VendorDeleteSummaryDeleted">
                        Deleted {deleted.length}
                    </Badge>
                    <Badge color="gray" variant="light" data-testid="VendorDeleteSummarySkipped">
                        Already removed {skipped.length}
                    </Badge>
                    <Badge color="red" variant="light" data-testid="VendorDeleteSummaryFailed">
                        Failed {failed.length}
                    </Badge>
                </Group>

                {failed.length > 0 ? (
                    <Box>
                        <Divider label="Failed" labelPosition="left" mb="xs" />
                        <List size="sm" spacing="xs">
                            {failed.map(({ candidate_id, error }) => (
                                <List.Item key={candidate_id}>
                                    <Text span fw={500}>
                                        {label(candidate_id)}
                                    </Text>{' '}
                                    <Text span c="dimmed">
                                        {error}
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    </Box>
                ) : null}
            </Stack>
        </Card>
    );
};

interface VendorRowProps {
    vendor: CatalogEntry;
    selected: boolean;
    onToggle: (id: number, checked: boolean) => void;
    onSelect: (candidateId: number) => void;
    disabled: boolean;
}

const VendorRow = ({
    vendor,
    selected,
    onToggle,
    onSelect,
    disabled,
}: VendorRowProps): React.JSX.Element => {
    // Provider abbreviation is only carried in the compound `provider:org:slug`
    // id (there's no dedicated field on /api/catalog); rows seeded without a
    // provider degrade to a leading empty segment, rendered as '—'.
    const provider = vendor.id.split(':')[0];
    const trustCenterHref = safeHttpUrl(vendor.trust_center_url);

    return (
        <Table.Tr
            style={{ cursor: 'pointer' }}
            onClick={() => onSelect(vendor.catalog_product_candidate_id)}
            data-testid="VendorRow"
        >
            <Table.Td
                onClick={(e) => {
                    // Keep a checkbox click from bubbling to the row's navigate handler.
                    e.stopPropagation();
                }}
            >
                <Checkbox
                    aria-label={`Select ${vendor.name}`}
                    checked={selected}
                    onChange={(e) => {
                        onToggle(vendor.catalog_product_candidate_id, e.currentTarget.checked);
                    }}
                    disabled={disabled}
                    data-testid="VendorRowCheckbox"
                />
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">
                    {vendor.catalog_product_candidate_id}
                </Text>
            </Table.Td>
            <Table.Td>{vendor.name}</Table.Td>
            <Table.Td>
                <Text size="sm" c={provider ? undefined : 'dimmed'}>
                    {provider || '—'}
                </Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm" c={vendor.product_name ? undefined : 'dimmed'}>
                    {vendor.product_name ?? '—'}
                </Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">
                    {vendor.domain ?? '—'}
                </Text>
            </Table.Td>
            <Table.Td>
                {vendor.is_default ? (
                    <Badge variant="light" color="blue" size="sm">
                        Default
                    </Badge>
                ) : (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                )}
            </Table.Td>
            <Table.Td>
                {trustCenterHref ? (
                    <Anchor
                        href={trustCenterHref}
                        target="_blank"
                        rel="noreferrer"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {trustCenterHref}
                    </Anchor>
                ) : (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                )}
            </Table.Td>
            <Table.Td>
                <Badge
                    variant={vendor.tenant_count ? 'light' : 'default'}
                    color={vendor.tenant_count ? 'teal' : 'gray'}
                >
                    {vendor.tenant_count}
                </Badge>
            </Table.Td>
            <Table.Td>
                <Badge
                    variant={vendor.cpe_mapping_count ? 'light' : 'default'}
                    color={vendor.cpe_mapping_count ? 'blue' : 'gray'}
                >
                    {vendor.cpe_mapping_count ?? 0}
                </Badge>
            </Table.Td>
            <Table.Td>
                <Badge
                    variant={vendor.cve_match_count ? 'light' : 'default'}
                    color={vendor.cve_match_count ? 'red' : 'gray'}
                >
                    {vendor.cve_match_count ?? 0}
                </Badge>
            </Table.Td>
        </Table.Tr>
    );
};
